import { type ReactNode, useEffect, useRef, useState } from "react";

type ViewPortProps = {
  children: ReactNode;
  offset?: number;
  mih?: number | string;
};

/**
 * Use this component to render the given children when reaching the viewport
 * It can be used to lazy load content specially that has API calls
 */
export function InViewport({ children, offset = 0, mih }: ViewPortProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasBeenVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      {
        root: null,
        rootMargin: `${offset}px`,
        threshold: 0,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [offset]);

  return (
    <div ref={elementRef} style={{ minHeight: mih }}>
      {(isVisible || hasBeenVisible) && children}
    </div>
  );
}
